import React from 'react';
import { CheckCircle2, Info } from 'lucide-react';

const VatProgressTracker = ({
    currentStep,
    stepOrder,
    stepTitles,
    stepIcons,
    isStepCompleted,
    isStepActive,
    onStepClick
}) => {
    const getCurrentStepIndex = () => {
        return stepOrder.indexOf(currentStep);
    };

    const steps = stepOrder.map((step, index) => ({
        id: index + 1,
        title: stepTitles[step].replace(/^Steg \d+: /, ''), // Remove "Steg X:" prefix
        description: getStepDescription(step),
        icon: stepIcons[step],
        key: step
    }));

    function getStepDescription(step) {
        const descriptions = {
            'instructions': 'Läs instruktioner och förbered',
            'review': 'Granska momsuppgifter',
            'submission': 'Skicka till Skatteverket',
            'payment': 'Hantera betalning',
            'record': 'Slutför bokföring'
        };
        return descriptions[step] || '';
    }

    const isStepAccessible = (stepId) => {
        return stepId <= getCurrentStepIndex() + 1;
    };

    return (
        <>
            <style>{`
                .vat-progress-tracker {
                    background: white;
                    border-radius: 12px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e5e7eb;
                }
                
                .vat-progress-header {
                    text-align: center;
                    margin-bottom: 2rem;
                }
                
                .vat-progress-title {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                }
                
                .vat-progress-subtitle {
                    color: #64748b;
                    font-size: 0.875rem;
                }
                
                .vat-steps-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    position: relative;
                    margin: 2rem 0;
                }
                
                .vat-progress-line {
                    position: absolute;
                    top: 30px;
                    left: 60px;
                    right: 60px;
                    height: 2px;
                    background: #e5e7eb;
                    z-index: 0;
                }
                
                .vat-progress-line-fill {
                    height: 100%;
                    background: #44814E;
                    border-radius: 1px;
                    transition: width 0.5s ease;
                }
                
                .vat-step-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    position: relative;
                    z-index: 1;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    min-width: 120px;
                    max-width: 140px;
                }
                
                .vat-step-item:hover {
                    transform: translateY(-2px);
                }
                
                .vat-step-item.disabled {
                    cursor: not-allowed;
                    opacity: 0.5;
                }
                
                .vat-step-item.disabled:hover {
                    transform: none;
                }
                
                .vat-step-circle {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 1rem;
                    position: relative;
                    transition: all 0.3s ease;
                    border: 3px solid transparent;
                }
                
                .vat-step-circle.completed {
                    background: linear-gradient(135deg, #44814E, #36673e); /* Changed to your green colors */
                    color: white;
                    box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3); /* Adjusted shadow color */
                }

                .vat-step-circle.active {
                    background: linear-gradient(135deg, #44814E, #3a6e42); /* Changed to your green colors */
                    color: white;
                    box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3); /* Adjusted shadow color */
                    border-color: rgba(68, 129, 78, 0.2); /* Adjusted border color */
                    animation: pulse 2s infinite;
            }
                
                .vat-step-circle.pending {
                    background: #f8fafc;
                    color: #9ca3af;
                    border: 2px solid #e5e7eb;
                }
                
                .vat-step-circle.accessible.pending:hover {
                    background: #f1f5f9;
                    border-color: #3b82f6;
                    color: #3b82f6;
                }
                
                @keyframes pulse {
                    0% {
                        box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3), 0 0 0 0 rgba(68, 129, 78, 0.4); /* Adjusted colors */
                    }
                    70% {
                        box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3), 0 0 0 10px rgba(68, 129, 78, 0); /* Adjusted colors */
                    }
                    100% {
                        box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3), 0 0 0 0 rgba(68, 129, 78, 0); /* Adjusted colors */
                    }
                }
                
                .vat-step-content {
                    flex: 1;
                }
                
                .vat-step-title {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 0.25rem;
                }
                
                .vat-step-description {
                    font-size: 0.75rem;
                    color: #64748b;
                    line-height: 1.4;
                }
                
                .vat-step-item.completed .vat-step-title {
                    color: #36673e;
                }
                
                .vat-step-item.active .vat-step-title {
                    color: #3a6e42;
                }
                
                .vat-step-item.completed .vat-step-description {
                    color: #44814E;
                }
                
                .vat-step-item.active .vat-step-description {
                    color: #44814E;
                }
                
                .vat-step-status {
                    margin-top: 0.5rem;
                    font-size: 0.75rem;
                    font-weight: 500;
                    padding: 0.25rem 0.75rem;
                    border-radius: 12px;
                    display: inline-block;
                }
                
                .vat-status-completed {
                    background: rgba(16, 185, 129, 0.15);
                    color: #059669;
                }
                
                .vat-status-active {
                    background: rgba(59, 130, 246, 0.25);
                    color: #3b82f6;
                }
                
                .vat-status-pending {
                    background: #f1f5f9;
                    color: #64748b;
                }
                
                /* Mobile responsive */
                @media (max-width: 768px) {
                    .vat-steps-container {
                        flex-direction: column;
                        gap: 1rem;
                    }
                    
                    .vat-progress-line {
                        display: none;
                    }
                    
                    .vat-step-item {
                        flex-direction: row;
                        text-align: left;
                        min-width: auto;
                        max-width: none;
                        width: 100%;
                        padding: 1rem;
                        background: #f8fafc;
                        border-radius: 8px;
                        border: 1px solid #e5e7eb;
                    }
                    
                    .vat-step-circle {
                        width: 50px;
                        height: 50px;
                        margin-bottom: 0;
                        margin-right: 1rem;
                        flex-shrink: 0;
                    }
                    
                    .vat-step-content {
                        flex: 1;
                    }
                    
                    .vat-step-status {
                        margin-top: 0.25rem;
                    }
                }
            `}</style>

            <div className="vat-progress-tracker">
                <div className="vat-progress-header">
                    <h3 className="vat-progress-title">Momsrapportering - Steg för steg</h3>
                    <p className="vat-progress-subtitle">
                        Följ dessa steg för att slutföra din momsrapport på ett säkert och korrekt sätt
                    </p>
                </div>

                <div className="vat-steps-container">
                    <div className="vat-progress-line">
                        <div
                            className="vat-progress-line-fill"
                            style={{
                                width: `${Math.max(0, (getCurrentStepIndex()) / (steps.length - 1) * 100)}%`
                            }}
                        />
                    </div>

                    {steps.map((step) => {
                        const completed = isStepCompleted(step.key);
                        const active = isStepActive(step.key);
                        const accessible = isStepAccessible(step.id);
                        const IconComponent = completed ? CheckCircle2 : step.icon;

                        return (
                            <div
                                key={step.key}
                                className={`vat-step-item ${completed ? 'completed' : active ? 'active' : 'pending'} ${!accessible ? 'disabled' : 'accessible'}`}
                                onClick={() => accessible && onStepClick && onStepClick(step.key)}
                            >
                                <div
                                    className={`vat-step-circle ${completed ? 'completed' : active ? 'active' : 'pending'} ${accessible ? 'accessible' : ''}`}
                                >
                                    <IconComponent size={24} />
                                </div>
                                <div className="vat-step-content">
                                    <div className="vat-step-title">
                                        {step.title}
                                    </div>
                                    <div className="vat-step-description">
                                        {step.description}
                                    </div>
                                    <div className={`vat-step-status ${completed ? 'vat-status-completed' : active ? 'vat-status-active' : 'vat-status-pending'}`}>
                                        {completed ? 'Slutförd' : active ? 'Aktiv' : 'Väntar'}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="text-center mt-3">
                    <small className="text-muted">
                        <Info size={16} className="me-1" />
                        Klicka på tillgängliga steg för att navigera direkt dit
                    </small>
                </div>
            </div>
        </>
    );
};

export default VatProgressTracker;